import { clsx } from 'clsx'
import { type FC } from 'react'
import { useIntl } from 'react-intl'

import {
  type UserOrderResponseWithFlagsSubOrdersInner,
  type UserSubOrderV2,
} from '@redteclab/api/clients/bully'

import { useGlobalConfigContext } from '../../global-config'
import {
  parcelLabAccessorGetParcelLabDates,
  parcelLabAccessorGetParcelLabDeliveredStatus,
  useApiParcelLabSwrGetOrderStatus,
} from '../../parcel-lab'
import { OrderSubOrderNowIndicator } from '../components/OrderSubOrderNowIndicator'
import { ApiContractAndParcelLabStatuses } from '../model/AccountOverviewLastOrderShippingStatusTypes'
import {
  userSubOrderAccessorGetStatusBasedOnEstimatedDate,
  userSubOrderAccessorIsCancellationRequested,
  userSubOrderAccessorIsNow,
  userSubOrderAccessorIsNowPickupOrder,
  userSubOrderGetNowOrderTimeRange,
} from '../model/userSubOrderAccessor'
import { useUserSubOrderShippingStatusLabels } from '../model/userSubOrderShippingStatusLabels'
import { userSubOrderShippingStatusMessageAndColor } from '../model/userSubOrderShippingStatusMessageAndColor'

export const OrderSubOrderStatus: FC<{
  includeNowIndicator?: boolean
  normalFont?: boolean
  subOrder: UserOrderResponseWithFlagsSubOrdersInner | UserSubOrderV2
}> = ({ includeNowIndicator, normalFont, subOrder }) => {
  const intl = useIntl()
  const shippingStatusLabels = useUserSubOrderShippingStatusLabels()

  const { language } = useGlobalConfigContext()

  const { data } = useApiParcelLabSwrGetOrderStatus(
    language,
    subOrder.carrier?.trackingId,
    subOrder.carrier?.externalCarrierIds?.parcellab,
  )

  const { deliveryDate, estimatedDate } = parcelLabAccessorGetParcelLabDates(
    intl,
    subOrder,
    data,
  )

  const orderDeliveredStatus = parcelLabAccessorGetParcelLabDeliveredStatus(
    subOrder,
    data,
  )

  if (!subOrder.enrichedStatus) {
    return null
  }

  const isNowOrder = userSubOrderAccessorIsNow(subOrder)

  const userSubOrderStatus = userSubOrderAccessorGetStatusBasedOnEstimatedDate({
    estimatedDate,
    orderDeliveredStatus,
    subOrderStatus: subOrder.enrichedStatus,
    timeRange: userSubOrderGetNowOrderTimeRange(subOrder),
  })

  const orderStatus = userSubOrderShippingStatusMessageAndColor({
    deliveryDate,
    isNowOrder,
    orderStatus: userSubOrderAccessorIsCancellationRequested(subOrder)
      ? ApiContractAndParcelLabStatuses.CancelRequested
      : userSubOrderStatus,
    shippingStatusLabels,
    timeRange: userSubOrderGetNowOrderTimeRange(subOrder),
  })

  return (
    <div className="text-start text-s">
      <p
        className={clsx(
          `flex font-medium ${orderStatus.color}`,
          normalFont ? 'font-normal' : 'font-bold',
        )}
        data-qa-id="order-status"
      >
        {orderStatus.message}
      </p>
      {!userSubOrderAccessorIsNowPickupOrder(subOrder) &&
        orderStatus.lastOrderOrderStatus ===
          ApiContractAndParcelLabStatuses.Shipped && (
          <p
            className={clsx('mb-2 font-bold', orderStatus.color)}
            data-qa-id="account-last-order-now-closed"
          >
            {userSubOrderGetNowOrderTimeRange(subOrder)}
          </p>
        )}
      {isNowOrder && includeNowIndicator ? <OrderSubOrderNowIndicator /> : null}
    </div>
  )
}
